import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { currentUserShowSuccess, fetchCurrentUser } from '../../ducks/user.duck';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { parse } from '../../util/urlHelpers';
import { trackEvent } from '../../util/analytics';

// ================ Action types ================ //

export const FETCH_FAVORITES_REQUEST = 'app/WishlistPage/FETCH_FAVORITES_REQUEST';
export const FETCH_FAVORITES_SUCCESS = 'app/WishlistPage/FETCH_FAVORITES_SUCCESS';
export const FETCH_FAVORITES_ERROR = 'app/WishlistPage/FETCH_FAVORITES_ERROR';

export const TOGGLE_FAVORITE_REQUEST = 'app/WishlistPage/TOGGLE_FAVORITE_REQUEST';
export const TOGGLE_FAVORITE_SUCCESS = 'app/WishlistPage/TOGGLE_FAVORITE_SUCCESS';
export const TOGGLE_FAVORITE_ERROR = 'app/WishlistPage/TOGGLE_FAVORITE_ERROR';

// ================ Action creators ================ //

export const fetchFavoritesRequest = () => ({
    type: FETCH_FAVORITES_REQUEST,
});

export const fetchFavoritesSuccess = (listingRefs, pagination) => ({
    type: FETCH_FAVORITES_SUCCESS,
    payload: { listingRefs, pagination },
});

export const fetchFavoritesError = error => ({
    type: FETCH_FAVORITES_ERROR,
    payload: error,
    error: true,
});

export const toggleFavoriteRequest = listingId => ({
    type: TOGGLE_FAVORITE_REQUEST,
    payload: { listingId },
});

export const toggleFavoriteSuccess = (listingId, wasRemoved) => ({
    type: TOGGLE_FAVORITE_SUCCESS,
    payload: { listingId, wasRemoved },
});

export const toggleFavoriteError = error => ({
    type: TOGGLE_FAVORITE_ERROR,
    payload: error,
    error: true,
});

// ================ Thunks ================ //

// Pagination page size
const RESULT_PAGE_SIZE = 24;

// Fetch favorite listings from user's publicData
export const fetchFavoriteListings = (page = 1, config) => {
    return (dispatch, getState, sdk) => {
        dispatch(fetchFavoritesRequest());

        const currentUser = getState().user.currentUser;
        const favorites = currentUser?.attributes?.profile?.publicData?.favorites || [];

        if (favorites.length === 0) {
            dispatch(fetchFavoritesSuccess([]));
            return Promise.resolve([]);
        }

        const allFavorites = [...favorites].reverse();

        const pagination = {
            page,
            perPage: RESULT_PAGE_SIZE,
            totalItems: allFavorites.length,
            totalPages: Math.ceil(allFavorites.length / RESULT_PAGE_SIZE),
        };

        const start = (page - 1) * RESULT_PAGE_SIZE;
        const end = start + RESULT_PAGE_SIZE;
        const pagedFavorites = allFavorites.slice(start, end);

        if (pagedFavorites.length === 0) {
            dispatch(fetchFavoritesSuccess([], pagination));
            return Promise.resolve([]);
        }

        // Use the same image config as SearchPage
        const {
            aspectWidth = 1,
            aspectHeight = 1,
            variantPrefix = 'listing-card',
        } = config?.layout?.listingImage || {};
        const aspectRatio = aspectHeight / aspectWidth;

        // Dynamically import createImageVariantConfig if config is available
        const imageVariantConfig = config
            ? require('../../util/sdkLoader').createImageVariantConfig
            : null;

        // Fetch listings by IDs
        return sdk.listings
            .query({
                ids: pagedFavorites,
                include: ['author', 'images', 'currentStock'],
                'fields.listing': [
                    'title',
                    'geolocation',
                    'price',
                    'createdAt',
                    'state',
                    'publicData.listingType',
                    'publicData.transactionProcessAlias',
                    'publicData.unitType',
                    'publicData.designer',
                    'publicData.en_designerLabel',
                    'publicData.el_designerLabel',
                    'publicData.department',
                    'publicData.en_departmentLabel',
                    'publicData.el_departmentLabel',
                    'publicData.category',
                    'publicData.en_categoryLabel',
                    'publicData.el_categoryLabel',
                    'publicData.subCategory',
                    'publicData.en_subCategoryLabel',
                    'publicData.el_subCategoryLabel',
                    'publicData.color',
                    'publicData.en_colorLabel',
                    'publicData.el_colorLabel',
                    'publicData.size',
                    'publicData.otherBrand',
                    'publicData.en_title',
                    'publicData.el_title',
                    'publicData.refrenceNumber',

                    // These help rendering of 'purchase' listings,
                    // when transitioning from search page to listing page
                    'publicData.pickupEnabled',
                    'publicData.shippingEnabled',
                ],
                // 'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
                'fields.stock': ['quantity'],
                'fields.image': [
                    'variants.scaled-small',
                    'variants.scaled-medium',
                    `variants.${variantPrefix}`,
                    `variants.${variantPrefix}-2x`,
                    'variants.default',
                ],
                'limit.images': 1,
            })
            .then(response => {
                const listingFields = config?.listing?.listingFields;
                const sanitizeConfig = { listingFields };

                // 1. Identify which IDs from pagedFavorites were NOT returned
                const returnedIds = response.data.data.map(l => l.id.uuid);
                const missingIds = pagedFavorites.filter(id => !returnedIds.includes(id));

                // 2. If there are missing IDs, fetch them individually via show()
                //    We map each missing ID to a promise that resolves to a response or null
                //    (in case it's truly gone/deleted or error occurs).
                if (missingIds.length > 0) {
                    const params = {
                        include: ['author', 'images', 'currentStock'],
                        'fields.listing': [
                            'title',
                            'geolocation',
                            'price',
                            'createdAt',
                            'state',
                            'publicData.listingType',
                            'publicData.transactionProcessAlias',
                            'publicData.unitType',
                            'publicData.designer',
                            'publicData.en_designerLabel',
                            'publicData.el_designerLabel',
                            'publicData.department',
                            'publicData.en_departmentLabel',
                            'publicData.el_departmentLabel',
                            'publicData.category',
                            'publicData.en_categoryLabel',
                            'publicData.el_categoryLabel',
                            'publicData.subCategory',
                            'publicData.en_subCategoryLabel',
                            'publicData.el_subCategoryLabel',
                            'publicData.color',
                            'publicData.en_colorLabel',
                            'publicData.el_colorLabel',
                            'publicData.size',
                            'publicData.otherBrand',
                            'publicData.en_title',
                            'publicData.el_title',
                            'publicData.refrenceNumber',
                            'publicData.pickupEnabled',
                            'publicData.shippingEnabled',
                        ],
                        'fields.stock': ['quantity'],
                        'fields.image': [
                            'variants.scaled-small',
                            'variants.scaled-medium',
                            `variants.${variantPrefix}`,
                            `variants.${variantPrefix}-2x`,
                            'variants.default',
                        ],
                        'limit.images': 1,
                    };

                    const fetchMissingPromises = missingIds.map(id =>
                        sdk.listings
                            .show({ ...params, id })
                            .then(res => ({ result: res, id, status: 'found' }))
                            .catch(e => {
                                // Identify if it's a 404 or other error meaning "not found/deleted"
                                // For now, simpler: if show fails, we assume it's gone or inaccessible.
                                // We can be more specific if needed (e.status === 404).
                                return { result: null, id, status: 'missing' };
                            })
                    );

                    return Promise.all(fetchMissingPromises).then(results => {
                        // Filter success
                        const fetchedMissingListings = results
                            .filter(r => r.status === 'found')
                            .map(r => r.result);

                        const deadIds = results
                            .filter(r => r.status === 'missing')
                            .map(r => r.id);

                        // Merge data into the main response
                        fetchedMissingListings.forEach(r => {
                            if (r.data && r.data.data) {
                                response.data.data.push(r.data.data);
                            }
                            if (r.data && r.data.included) {
                                if (!response.data.included) {
                                    response.data.included = [];
                                }
                                response.data.included.push(...r.data.included);
                            }
                        });

                        // If we found dead IDs, remove them from user profile
                        if (deadIds.length > 0) {
                            // Calculate new favorites list
                            // existing 'favorites' variable is from closure (getState().user.currentUser...)
                            // We need to ensure we have the latest list, but 'favorites' from line 61 should be recent enough for this fetch.
                            const updatedFavorites = favorites.filter(fid => !deadIds.includes(fid));

                            return sdk.currentUser.updateProfile({
                                publicData: {
                                    favorites: updatedFavorites,
                                },
                            }).then(profileResp => {
                                // Update Redux with new profile data to reflect changes immediately
                                // We fetch the full user object again to ensure all fields (like profile image) are present
                                // and preventing crashes in other components.
                                return dispatch(fetchCurrentUser());
                            }).then(() => {
                                // Recursively fetch the favorite listings again.
                                return dispatch(fetchFavoriteListings(page, config));
                            }).catch(e => {
                                // Silently fail profile update, just log or ignore.
                                // We don't want to break the page load if this background cleanup fails.
                                console.warn('Failed to cleanup dead wishlist items', e);
                                // Fallback to showing what we have if cleanup fails
                                dispatch(addMarketplaceEntities(response, sanitizeConfig));
                                const listingRefs = response.data.data.map(l => ({ type: 'listing', id: l.id }));
                                dispatch(fetchFavoritesSuccess(listingRefs, pagination));
                                return response;
                            });
                        }

                        dispatch(addMarketplaceEntities(response, sanitizeConfig));
                        // Filter out dead IDs from listingRefs so they don't show as empty/broken cards
                        // The response.data.data only contains valid listings (original query + successfull show())
                        const validListingIds = response.data.data.map(l => l.id.uuid);
                        // Sort listing refs to match pagedFavorites order (newest first)
                        const listingRefs = pagedFavorites
                            .filter(fId => validListingIds.includes(fId))
                            .map(fId => {
                                const listing = response.data.data.find(l => l.id.uuid === fId);
                                return { type: 'listing', id: listing.id };
                            });

                        dispatch(fetchFavoritesSuccess(listingRefs, pagination));
                        return response;
                    });
                } else {
                    dispatch(addMarketplaceEntities(response, sanitizeConfig));
                    // Sort listing refs to match pagedFavorites order (newest first)
                    const returnedIds = response.data.data.map(l => l.id.uuid);
                    const listingRefs = pagedFavorites
                        .filter(fId => returnedIds.includes(fId))
                        .map(fId => {
                            const listing = response.data.data.find(l => l.id.uuid === fId);
                            return { type: 'listing', id: listing.id };
                        });
                    dispatch(fetchFavoritesSuccess(listingRefs, pagination));
                    return response;
                }
            })
            .catch(e => {
                dispatch(fetchFavoritesError(storableError(e)));
                throw e;
            });
    };
};

// Toggle favorite status for a listing
export const toggleFavorite = listingId => {
    return (dispatch, getState, sdk) => {
        dispatch(toggleFavoriteRequest(listingId));

        const currentUser = getState().user.currentUser;
        const currentFavorites = currentUser?.attributes?.profile?.publicData?.favorites || [];

        const isFavorited = currentFavorites.includes(listingId);
        const updatedFavorites = isFavorited
            ? currentFavorites.filter(id => id !== listingId)
            : [...currentFavorites, listingId];

        return sdk.currentUser
            .updateProfile(
                {
                    publicData: {
                        favorites: updatedFavorites,
                    },
                },
                {
                    expand: true,
                    include: ['profileImage'],
                    'fields.image': ['variants.square-small', 'variants.square-small2x'],
                }
            )
            .then(response => {
                // Pass whether this was a removal (unfavorite) action
                dispatch(toggleFavoriteSuccess(listingId, isFavorited));

                const entities = denormalisedResponseEntities(response);
                if (entities.length !== 1) {
                    throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
                }
                const currentUser = entities[0];

                // Update current user in state.user.currentUser through user.duck.js
                dispatch(currentUserShowSuccess(currentUser));

                if (!isFavorited) {
                    trackEvent('add_to_wishlist', { product_id: listingId });
                }

                return response;
            })
            .catch(e => {
                dispatch(toggleFavoriteError(storableError(e)));
                throw e;
            });
    };
};

// Load data for WishlistPage
export const loadData = (params, search, config) => {
    const queryParams = parse(search);
    const page = queryParams.page || 1;
    return fetchFavoriteListings(page, config);
};
